<template>
  <el-form
      @submit.prevent="validateSubmit"
      novalidate
      :model="itemMissingSerials"
      ref="inventoryAdjustmentForm"
      label-position="top"
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.saveChanges')"
              isSuccess
              faIcon="save"
              @click.prevent="validateSubmit('inventoryAdjustmentForm')"
          />
        </div>

        <div class="md:w-4/12 m-2">
          <horizontal-button
              :title="$t('actions.clearButton')"
              isDanger
              faIcon="trash"
              @click.prevent="clearForm('inventoryAdjustmentForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section class="my-2">
      <div class="text-xl font-bold">{{ t('addMissingSerials') }}</div>
      <div class="mt-2">{{ t('userAdjusting') }}: <span class="font-bold">{{ user.fullName }}</span></div>
      <div>{{ t('itemName') }}: <span class="font-bold">{{ itemMissingSerials.name }}</span></div>
      <div>{{ t('location') }}: <span class="font-bold">{{
          $ctx.locale == 'en' ? itemMissingSerials.location_en_name : itemMissingSerials.location_ar_name
        }}</span></div>

      <el-form-item v-if="itemMissingSerials" :label="t('numberOfSerialsToBeAdded')" class="input-label" prop="reason">
        <el-input-number
            class="add-missing"
            v-model="itemMissingSerials.new_added_count"
            required
            :step="1"
            :min="0"
            :max="itemMissingSerials.quantity - itemMissingSerials.total_serials"
            controls-position="right"
        >
        </el-input-number>
      </el-form-item>

      <tag-input
          v-if="itemMissingSerials.new_added_count > 0"
          v-model="itemMissingSerials.serials"
          :insertFunction="(val) => itemMissingSerials.insertSerialNumber(val)"
          :removeFunction="(index) => itemMissingSerials.removeSerialNumber(index)"
          :validate="() => itemMissingSerials.validateSerials()"
      />
    </input-section>


  </el-form>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import InventoryAdjustment from '@/models/inventory/InventoryAdjustment'
import {ElForm} from 'element-plus'

// components
import AlertBadge from '@/components/AlertBadge.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import TagInput from '@/components/form/TagInput.vue'
import {useI18n} from 'vue-i18n'
import {ItemMissingSerials} from "@/models/inventory/ItemMissingSerials";

export default defineComponent({
  name: 'new-inventory-receiving',

  setup() {
    const {t} = useI18n({
      messages: {
        en: {
          addMissingSerials: 'Add Missing Serials',
          itemName: 'Item Name',
          location: 'Store Name',
          numberOfSerialsToBeAdded: 'Number Of Serials To Be Added',
          userAdjusting: 'Employee responsible for this inventory adjustment',
          insertSerials: 'You must insert {num} new serial numbers',
          successMsg: 'Missing serials was added successfully'
        },
        ar: {
          addMissingSerials: 'اضافة الارقام التسلسلية',
          itemName: 'اسم المادة',
          location: 'اسم المتجر',
          numberOfSerialsToBeAdded: 'عدد الارقام التسلسلية المراد اضافتها',
          userAdjusting: 'الموظف المسؤول عن تعديل البضاعة',
          insertSerials: 'قم بادخال {num} serial numbers',
          successMsg: 'تم اضافة الارقام التسلسلية المفقودة بنجاح'
        }
      }
    })

    return {t}
  },

  components: {
    AlertBadge,
    HorizontalButton,
    TagInput,
    InputSection
  },
  data() {
    return {
      itemMissingSerials: new ItemMissingSerials(),
      itermSearchTerm: '',
      error: {title: '', body: ''},
      serials: [],
      pressedEnter: false,
      user: this.$ctx.currentUser,
    }
  },

  mounted() {
    this.$http.get<ItemMissingSerials>(`${ItemMissingSerials.ENDPOINT}/${this.$route.params.id}?location_id=${this.$route.query.location_id}`).then(data => {
      this.itemMissingSerials = ItemMissingSerials.from(data);
    })
  },

  methods: {
    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
      this.itemMissingSerials.new_added_count = 0;
      this.itemMissingSerials.serials = [];
    },

    async submit(im: ItemMissingSerials) {
      this.error.title = ''
      this.error.body = ''

      try {
        await this.$http.post(ItemMissingSerials.ENDPOINT, im)
        await this.$router.push(this.$Route.INVENTORY_INVENTORY_ADJUSTMENTS)
      } catch (error) {
        return error
      }
    },

    checkForm() {
      const errors = []
      errors.push(this.itemMissingSerials.validateSerials())
      this.error.body = errors.filter((err) => err).join('\n')

      if (this.error.body) {
        this.error.title = this.$t('validation.inputErrors')
        document.getElementById('main')?.scrollTo({top: 0})
        return false
      }

      return true
    },

    async validateSubmit(formName: string) {
      const form = this.$refs[formName] as typeof ElForm

      form?.validate(async (valid: boolean) => {
        if (!valid || !this.checkForm()) {
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        }

        const resp = await this.submit(this.itemMissingSerials)

        if (resp) {
          this.error.title = resp?.title
          this.error.body = resp?.body
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        } else {
          this.clearForm(formName)
          this.$alertModal.showSuccess({ title: this.t('successMsg') })
        }
        return true
      })
    }
  }
})
</script>

<style scoped>
form > h1 {
  @apply p-2 text-lg py-3 font-bold;
}

form > h2 {
  @apply p-2 py-3 font-bold;
}

form > h3 {
  @apply p-2 py-3 font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>
